import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CloudArrowUpIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import { firestore } from '../firebase';
import { uploadToS3 } from '../aws-config';
import Alert from './Alert';
import Confirm from './Confirm';
import PermissionGate from './PermissionGate';

const PLACEHOLDER_CATEGORIES = [
  { categoryId: 'bubble-tea', label: 'Bubble Tea', placeholder: 'BT' },
  { categoryId: 'coffee', label: 'Coffee', placeholder: 'CF' },
  { categoryId: 'food', label: 'Food', placeholder: 'FD' },
  { categoryId: 'desserts', label: 'Desserts', placeholder: 'DS' },
  { categoryId: 'hot-drinks', label: 'Hot Drinks', placeholder: 'HD' },
  { categoryId: 'refreshers', label: 'Refreshers', placeholder: 'RF' },
];

function emptyForm() {
  return {
    categoryId: '',
    label: '',
    iconUrl: '',
    placeholder: '',
  };
}

function validateSvg(file) {
  if (!file) return;
  const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
  if (!isSvg) {
    throw new Error('Please upload an SVG file.');
  }
  if (file.size > 512 * 1024) {
    throw new Error('SVG icon must be 512KB or smaller.');
  }
}

export default function CategoryIcons() {
  const fileInputRef = useRef(null);
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmCallback, setConfirmCallback] = useState(null);

  useEffect(() => {
    const unsubscribe = firestore
      .collection('menuCategoryIcons')
      .orderBy('label')
      .onSnapshot((snapshot) => {
        setRecords(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      });
    return () => unsubscribe();
  }, []);

  const mergedRows = useMemo(() => {
    const byCategoryId = new Map(records.map((record) => [record.categoryId, record]));
    const placeholders = PLACEHOLDER_CATEGORIES
      .filter((category) => !byCategoryId.has(category.categoryId))
      .map((category) => ({
        ...category,
        id: category.categoryId,
        iconUrl: '',
        placeholderOnly: true,
      }));
    return [...records, ...placeholders].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [records]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const startEdit = (record) => {
    setEditingId(record.placeholderOnly ? '' : record.id);
    setForm({
      categoryId: record.categoryId || record.id || '',
      label: record.label || '',
      iconUrl: record.iconUrl || '',
      placeholder: record.placeholder || '',
    });
  };

  const resetForm = () => {
    setEditingId('');
    setForm(emptyForm());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      validateSvg(file);
      setUploading(true);
      const url = await uploadToS3(file, 'category-icons');
      updateField('iconUrl', url);
      setAlertMessage('SVG icon uploaded.');
    } catch (error) {
      setAlertMessage(error.message || 'Unable to upload SVG icon.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const categoryId = form.categoryId.trim();
    const label = form.label.trim();
    if (!categoryId || !label) {
      setAlertMessage('Category ID and label are required.');
      return;
    }

    setSaving(true);
    try {
      const docId = editingId || categoryId;
      await firestore.collection('menuCategoryIcons').doc(docId).set({
        categoryId,
        label,
        iconUrl: form.iconUrl.trim(),
        placeholder: form.placeholder.trim() || label.slice(0, 2).toUpperCase(),
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setAlertMessage('Category icon saved.');
      resetForm();
    } catch (error) {
      setAlertMessage(error.message || 'Unable to save category icon.');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (record) => {
    if (record.placeholderOnly) return;
    setConfirmMessage(`Delete icon config for ${record.label}?`);
    setConfirmCallback(() => async () => {
      await firestore.collection('menuCategoryIcons').doc(record.id).delete();
      setConfirmMessage(null);
      setAlertMessage('Category icon deleted.');
    });
  };

  return (
    <div className="m-auto p-6 md:p-10 max-w-screen-xl">
      {alertMessage && <Alert message={alertMessage} onClose={() => setAlertMessage(null)} />}
      {confirmMessage && (
        <Confirm
          message={confirmMessage}
          onConfirm={confirmCallback}
          onCancel={() => setConfirmMessage(null)}
        />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Menu Category Icons</h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload SVG icons against category IDs used by native ordering.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6">
        <form onSubmit={handleSave} className="bg-white rounded shadow p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Category ID</label>
            <input
              value={form.categoryId}
              onChange={(e) => updateField('categoryId', e.target.value)}
              placeholder="bubble-tea"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Display Label</label>
            <input
              value={form.label}
              onChange={(e) => updateField('label', e.target.value)}
              placeholder="Bubble Tea"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Placeholder Initials</label>
            <input
              value={form.placeholder}
              onChange={(e) => updateField('placeholder', e.target.value)}
              placeholder="BT"
              maxLength={4}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">SVG Icon</label>
            <div className="flex items-center gap-3">
              <PermissionGate permission="content.category_icons.upload"><button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 bg-teal-500 text-white px-4 py-2 rounded disabled:opacity-60"
              >
                {uploading ? <ArrowPathIcon className="size-5 animate-spin" /> : <CloudArrowUpIcon className="size-5" />}
                {uploading ? 'Uploading' : 'Upload SVG'}
              </button></PermissionGate>
              <input ref={fileInputRef} type="file" accept=".svg,image/svg+xml" onChange={handleUpload} className="hidden" />
              {form.iconUrl && (
                <img src={form.iconUrl} alt="" className="w-10 h-10 rounded border object-contain p-1" />
              )}
            </div>
            <input
              value={form.iconUrl}
              onChange={(e) => updateField('iconUrl', e.target.value)}
              placeholder="https://..."
              className="w-full border rounded px-3 py-2 mt-3 text-sm"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <PermissionGate permission={editingId ? 'content.category_icons.update' : 'content.category_icons.create'}><button
              type="submit"
              disabled={saving || uploading}
              className="bg-teal-500 text-white px-4 py-2 rounded disabled:opacity-60"
            >
              {saving ? 'Saving...' : editingId ? 'Update Icon' : 'Save Icon'}
            </button></PermissionGate>
            <button type="button" onClick={resetForm} className="border px-4 py-2 rounded">
              Reset
            </button>
          </div>
        </form>

        <div className="bg-white rounded shadow overflow-hidden">
          <table className="min-w-full text-left">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Preview</th>
                <th className="py-2 px-4 border-b">Category</th>
                <th className="py-2 px-4 border-b hidden md:table-cell">Category ID</th>
                <th className="py-2 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mergedRows.map((record) => (
                <tr key={`${record.categoryId}-${record.placeholderOnly ? 'placeholder' : 'saved'}`}>
                  <td className="py-3 px-4 border-b">
                    {record.iconUrl ? (
                      <img src={record.iconUrl} alt="" className="w-11 h-11 rounded border object-contain p-1" />
                    ) : (
                      <div className="w-11 h-11 rounded border bg-teal-50 text-teal-600 flex items-center justify-center font-bold">
                        {record.placeholder || record.label?.slice(0, 2).toUpperCase() || 'GT'}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 border-b">
                    <div className="font-semibold">{record.label}</div>
                    {record.placeholderOnly && <div className="text-xs text-gray-500">Placeholder</div>}
                  </td>
                  <td className="py-3 px-4 border-b hidden md:table-cell font-mono text-sm">{record.categoryId}</td>
                  <td className="py-3 px-4 border-b">
                    <PermissionGate permission="content.category_icons.update"><button onClick={() => startEdit(record)} className="text-teal-500 mr-3 hover:opacity-70">
                      <PencilSquareIcon className="size-7" />
                    </button></PermissionGate>
                    {!record.placeholderOnly && (
                      <PermissionGate permission="content.category_icons.delete"><button onClick={() => requestDelete(record)} className="text-red-500 hover:opacity-70">
                        <TrashIcon className="size-7" />
                      </button></PermissionGate>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
